import React, { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { Workflow, Plus, Search, MoreHorizontal, Edit, Archive } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { LoadingSpinner } from "@/components/loading-spinner";

export default function WorkflowsDashboard() {
  const workflows = useQuery(api.workflows.list);
  const archiveWorkflow = useMutation(api.workflows.archive);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  if (workflows === undefined) {
    return (
      <DashboardLayout activeTab="Workflows">
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    );
  }

  const filteredWorkflows = workflows.filter((w) =>
    (w.name || "Untitled Workflow").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout activeTab="Workflows">
      <div className="space-y-6 py-4">
        {/* Header */}
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Workflows</h1>
            <p className="mt-1 text-gray-500">
              Build and manage dynamic automations.
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="gap-2" onClick={() => navigate("/workflows/archived")}>
              <Archive className="h-4 w-4" /> Archived Workflows
            </Button>
            <Button
              className="flex items-center gap-2"
              onClick={() => navigate("/workflows/builder")}
            >
              <Plus className="h-4 w-4" />
              New Workflow
            </Button>
          </div>
        </div>

        <Card className="w-full p-6">
          <div className="mb-6 flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <Input
                placeholder="Search workflows..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="w-full overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Workflow Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWorkflows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <EmptyState
                        icon={Workflow}
                        title="No workflows yet"
                        description="Create your first workflow to automate your processes."
                        actionLabel="Create Workflow"
                        onAction={() => navigate("/workflows/builder")}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredWorkflows.map((workflow) => (
                    <TableRow key={workflow._id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="rounded-lg bg-blue-50 p-2">
                            <Workflow className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="font-medium text-gray-900">
                            {workflow.name || "Untitled Workflow"}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[300px] truncate text-gray-500">
                          {workflow.description || "No description provided."}
                        </div>
                      </TableCell>
                      <TableCell>
                        {workflow.isActive ? (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">
                            Draft
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">
                          {workflow._creationTime ? format(new Date(workflow._creationTime), "MMM d, yyyy") : "Unknown"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => navigate(`/workflows/builder?id=${workflow._id}`)}
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              Edit Workflow
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => archiveWorkflow({ id: workflow._id })}
                              className="text-orange-600"
                            >
                              <Archive className="mr-2 h-4 w-4" />
                              Archive
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
